/**
 * Heurístico para extraer el "apellido" de un string de autor tal como llega
 * del `<dc:creator>` de un EPUB, sin mutar el original.
 *
 * Pensado para alimentar el bucket de letra del índice alfabético en modo
 * "Autores": `alphaBucket(authorLastName(book.author))`.
 *
 * Cubre los tres formatos reales del `<dc:creator>`:
 *   1. Biblioteca: `"King, Stephen"` → apellido antes de la coma.
 *   2. Nombre simple: `"Stephen King"` → última palabra significativa,
 *      saltando partículas nobiliarias/de unión y las iniciales.
 *   3. Solo iniciales/partículas o token único → el string entero.
 *
 * Limitación documentada: apellidos compuestos hispanos como
 * "Mario Vargas Llosa" → "Llosa" (se agrupa bajo L, no bajo "Vargas Llosa").
 * Es un compromiso deliberado por predictibilidad; detectar correctamente
 * el apellido compuesto requeriría una base de datos de apellidos.
 */

// Partículas/de unión que NO son el apellido. Minúsculas. Incluye variantes
// romances (de/del/da/do/di...) y germánicas (von/van/der...), más las
// conjunciones hispanas (y/i) y el árabe (bin/ibn/al).
const PARTICLES = new Set([
  'de', 'del', 'della', 'delle', 'dello', 'lo', 'los', 'la', 'las',
  'da', 'do', 'dos', 'di', 'dal', 'du', 'des',
  'von', 'van', 'der', 'den', 'ten', 'zur', 'zu',
  'le', 'el', 'y', 'i', 'bin', 'ibn', 'al',
])

/**
 * Devuelve el apellido (sort-key) de un string de autor, sin normalizar
 * acentos ni mayúsculas (eso lo hace `alphaBucket` encima).
 *
 * Exportada como función pura para poder testearla aislada.
 */
export function authorLastName(author: string | null | undefined): string {
  const raw = (author ?? '').trim()
  if (!raw) return ''

  // 1. Formato "Apellido, Nombre" → lo anterior a la primera coma.
  const commaIdx = raw.indexOf(',')
  if (commaIdx > 0) {
    const last = raw.slice(0, commaIdx).trim()
    return last || raw
  }

  // 2. Nombre simple: última palabra que no sea partícula ni inicial.
  const tokens = raw.split(/\s+/).filter(Boolean)
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i]
    const norm = token.toLowerCase().replace(/[.]/g, '')
    if (!norm) continue
    if (PARTICLES.has(norm)) continue
    // Inicial "J." o "J" (una sola letra, con o sin punto).
    if (/^[a-zà-ÿ]$/i.test(token.replace(/\./g, ''))) continue
    return token
  }

  // 3. Solo quedan iniciales/partículas (o un solo token) → string entero.
  return raw
}
